import { combinedLanguageCharacterSet } from "./language-character-set";

/*
  Validation summary:
  - Overall: 1–80 chars; no consecutive dots
  - Local part: ASCII letters (A–Z, a–z), digits (0–9), and characters from allowedEmailLocalPartSpecialChars. Must not start with or end with a dot.
  - Domain part: Letters from combinedLanguageCharacterSet and digits; labels may contain internal hyphens; dot-separated labels; single-character intermediate labels are allowed
  - TLD part: Final label must be ASCII letters only (A–Z, a–z), minimum length 2

  /^(?=.{1,80}$)(?!.*\.\.)(?!\.)[A-Za-z\d._+!#$%&*'/=?`{|}~^-]+(?<!\.)@(?:[A-Za-zĄąĆćĘęŁłŃńÓóŚśŹźŻżÁáČčĎďÉéĚěÍíŘřŠšŤťÚúŮůÝýŽžÄäÖöÜüẞßÀàÂâÆæÇçÉéÈèÊêËëÎîÏïÔôŒœÙùÛûÜüŸÿÀàÈèÉéÌìÒòÓóÙùÇçÁáÉéÍíÑñÓóÚúÜüÇçĞğİıÖöŞşÜüÆæØøÅå\d](?:[A-Za-zĄąĆćĘęŁłŃńÓóŚśŹźŻżÁáČčĎďÉéĚěÍíŘřŠšŤťÚúŮůÝýŽžÄäÖöÜüẞßÀàÂâÆæÇçÉéÈèÊêËëÎîÏïÔôŒœÙùÛûÜüŸÿÀàÈèÉéÌìÒòÓóÙùÇçÁáÉéÍíÑñÓóÚúÜüÇçĞğİıÖöŞşÜüÆæØøÅå\d\-]*[A-Za-zĄąĆćĘęŁłŃńÓóŚśŹźŻżÁáČčĎďÉéĚěÍíŘřŠšŤťÚúŮůÝýŽžÄäÖöÜüẞßÀàÂâÆæÇçÉéÈèÊêËëÎîÏïÔôŒœÙùÛûÜüŸÿÀàÈèÉéÌìÒòÓóÙùÇçÁáÉéÍíÑñÓóÚúÜüÇçĞğİıÖöŞşÜüÆæØøÅå\d])?(?:\.[A-Za-zĄąĆćĘęŁłŃńÓóŚśŹźŻżÁáČčĎďÉéĚěÍíŘřŠšŤťÚúŮůÝýŽžÄäÖöÜüẞßÀàÂâÆæÇçÉéÈèÊêËëÎîÏïÔôŒœÙùÛûÜüŸÿÀàÈèÉéÌìÒòÓóÙùÇçÁáÉéÍíÑñÓóÚúÜüÇçĞğİıÖöŞşÜüÆæØøÅå\d]{1,}(?:[A-Za-zĄąĆćĘęŁłŃńÓóŚśŹźŻżÁáČčĎďÉéĚěÍíŘřŠšŤťÚúŮůÝýŽžÄäÖöÜüẞßÀàÂâÆæÇçÉéÈèÊêËëÎîÏïÔôŒœÙùÛûÜüŸÿÀàÈèÉéÌìÒòÓóÙùÇçÁáÉéÍíÑñÓóÚúÜüÇçĞğİıÖöŞşÜüÆæØøÅå\d\-]*[A-Za-zĄąĆćĘęŁłŃńÓóŚśŹźŻżÁáČčĎďÉéĚěÍíŘřŠšŤťÚúŮůÝýŽžÄäÖöÜüẞßÀàÂâÆæÇçÉéÈèÊêËëÎîÏïÔôŒœÙùÛûÜüŸÿÀàÈèÉéÌìÒòÓóÙùÇçÁáÉéÍíÑñÓóÚúÜüÇçĞğİıÖöŞşÜüÆæØøÅå\d])?)*\.[A-Za-z]{2,})$/u
*/

const unicodeAlnum = `[${combinedLanguageCharacterSet}\\d]`;

const allowedEmailLocalPartSpecialChars = `._+!#$%&*'/=?\`{|}~^-`;
const localPartChars = `[A-Za-z\\d${allowedEmailLocalPartSpecialChars}]`;

const domainLabelTail = `[${combinedLanguageCharacterSet}\\d\\-]*${unicodeAlnum}`;
const domainLabel = `${unicodeAlnum}(?:${domainLabelTail})?`;
const asciiTld = `[A-Za-z]{2,}`;

const domainPart =
  `${domainLabel}` +
  `(?:\\.${unicodeAlnum}{1,}(?:${domainLabelTail})?)*` +
  `\\.${asciiTld}`;

const emailRegexSource =
  `^(?=.{1,80}$)(?!.*\\.\\.)` +
  `(?!\\.)` +
  `${localPartChars}+` +
  `(?<!\\.)@` +
  `(?:${domainPart})$`;

export const emailValidationPattern = new RegExp(emailRegexSource, "u");

/*
  Account recovery (forgot password): more permissive than emailValidationPattern.
  Uses Unicode letter class for domain labels; max length 80 to match registration.
*/
export const accountRecoveryEmailValidationPattern =
  /^(?=.{1,80}$)[\p{L}\d_\-\+\.!#$%&'*\/=?`{|}~^]+@(?:[\p{L}\d](?:[\p{L}\d\-]*[\p{L}\d])?(?:\.[\p{L}\d](?:[\p{L}\d\-]*[\p{L}\d])?)+)$/u;
